import React, { useState } from "react";
import {
  Paper,
  Typography,
  Button,
  TextField,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { submitFeedback } from "../../services/api";
import { t } from "../../translations";

interface FeedbackFormProps {
  sessionId: string;
  onClose: () => void;
  mode: "report" | "feedback";
  language: string;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  sessionId,
  onClose,
  mode,
  language,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [experience, setExperience] = useState<number | null>(null);
  const [usability, setUsability] = useState<number | null>(null);
  const [openRemarks, setOpenRemarks] = useState("");
  const [recommend, setRecommend] = useState<number | null>(null);
  const [jobMatchSatisfaction, setJobMatchSatisfaction] = useState<
    number | null
  >(null);
  const [issueDescription, setIssueDescription] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (mode === "report") {
        // issue report
        await submitFeedback({
          session_id: sessionId,
          reported_issues: issueDescription,
          reporter_email: reporterEmail || undefined,
        });
      } else {
        // regular feedback
        await submitFeedback({
          session_id: sessionId,
          experience_rating: experience,
          usability_rating: usability,
          recommend_rating: recommend,
          job_match_satisfaction: jobMatchSatisfaction,
          open_remarks: openRemarks,
        });
      }
      setShowThankYou(true);
    } catch (err: any) {
      console.error("Error submitting feedback:", err);
      alert("Something went wrong while submitting feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBubbleScale = (
    label: string,
    labelLeft: string,
    labelRight: string,
    value: number | null,
    onChange: (val: number) => void
  ) => (
    <div style={{ marginBottom: "24px" }}>
      <Typography
        variant="body1"
        sx={{
          fontSize: { xs: "15px", sm: "16px", md: "16px" },
          marginBottom: { xs: "16px", sm: "16px" },
          fontWeight: 600,
          color: "#333",
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>

      {/* Mobile: Slider */}
      {isMobile && (
        <div style={{ display: "block" }}>
          <div style={{ paddingLeft: "16px", paddingRight: "16px", marginBottom: "16px" }}>
            {/* Selected value display */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "48px",
                  fontWeight: "bold",
                  color: value !== null ? "#003366" : "#ccc",
                  transition: "color 0.3s ease",
                }}
              >
                {value !== null ? value : "—"}
              </Typography>
            </div>

            {/* Slider */}
            <div style={{ position: "relative", marginBottom: "8px" }}>
              <input
                type="range"
                min="0"
                max="10"
                value={value ?? 5}
                onChange={(e) => onChange(Number(e.target.value))}
                style={{
                  width: "100%",
                  height: "8px",
                  borderRadius: "4px",
                  outline: "none",
                  background:
                    value !== null
                      ? `linear-gradient(to right, #1976d2 0%, #1976d2 ${
                          (value / 10) * 100
                        }%, #e0e0e0 ${(value / 10) * 100}%, #e0e0e0 100%)`
                      : "#e0e0e0",
                  WebkitAppearance: "none",
                  appearance: "none",
                }}
              />
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 32px;
                  height: 32px;
                  border-radius: 50%;
                  background: #1976d2;
                  cursor: pointer;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                  transition: transform 0.2s ease;
                }
                input[type="range"]::-webkit-slider-thumb:active {
                  transform: scale(1.2);
                }
                input[type="range"]::-moz-range-thumb {
                  width: 32px;
                  height: 32px;
                  border-radius: 50%;
                  background: #1976d2;
                  cursor: pointer;
                  border: none;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                  transition: transform 0.2s ease;
                }
                input[type="range"]::-moz-range-thumb:active {
                  transform: scale(1.2);
                }
              `}</style>
            </div>

            {/* Scale markers */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingLeft: "4px",
                paddingRight: "4px",
                marginBottom: "16px",
              }}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <Typography
                  key={num}
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    color: value === num ? "#1976d2" : "#999",
                    fontWeight: value === num ? "bold" : "normal",
                  }}
                >
                  {num}
                </Typography>
              ))}
            </div>
          </div>

          {/* Labels */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingLeft: "8px",
              paddingRight: "8px",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {labelLeft}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {labelRight}
            </Typography>
          </div>
        </div>
      )}

      {/* Desktop: Bubble buttons */}
      {!isMobile && (
        <div style={{ display: "block" }}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              flexWrap: "nowrap",
              marginBottom: "5px",
            }}
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <Button
                key={num}
                variant={value === num ? "contained" : "outlined"}
                onClick={() => onChange(num)}
                sx={{
                  width: { md: "42px", lg: "44px" },
                  height: { md: "42px", lg: "44px" },
                  minWidth: { md: "42px", lg: "44px" },
                  maxWidth: { md: "42px", lg: "44px" },
                  borderRadius: "50%",
                  padding: 0,
                  fontSize: "14px",
                  fontWeight: value === num ? "bold" : "normal",
                  flexShrink: 0,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.08)",
                  },
                  boxShadow:
                    value === num ? "0 3px 10px rgba(0, 51, 102, 0.3)" : "none",
                }}
              >
                {num}
              </Button>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingLeft: "8px",
              paddingRight: "8px",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {labelLeft}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {labelRight}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );

  // Thank You Popup
  if (showThankYou) {
    return (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 49,
            backdropFilter: "blur(2px)",
          }}
          onClick={() => {
            setShowThankYou(false);
            onClose();
          }}
        />
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 50,
            width: { xs: "90%", sm: "85%", md: "400px" },
            maxWidth: "450px",
            p: { xs: 3, sm: 4 },
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(10px)",
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#003366",
              fontWeight: "bold",
              marginBottom: { xs: 1.5, sm: 2 },
              fontSize: { xs: "20px", sm: "24px" },
            }}
          >
            {t("thankYou", language)} 🎉
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#555",
              marginBottom: { xs: 2.5, sm: 3 },
              fontSize: { xs: "14px", sm: "15px" },
              lineHeight: 1.6,
            }}
          >
            {mode === "report"
              ? t("thankYouIssue", language)
              : t("thankYouFeedback", language)}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowThankYou(false);
              onClose();
            }}
            sx={{
              paddingX: { xs: 3, sm: 4 },
              paddingY: { xs: 1, sm: 1.25 },
              fontSize: { xs: "14px", sm: "15px" },
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            {t("close", language)}
          </Button>
        </Paper>
      </>
    );
  }

  // Main Feedback Form
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 49,
          backdropFilter: "blur(2px)",
        }}
        onClick={onClose}
      />
      <Paper
        elevation={12}
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 50,
          width: { xs: "94%", sm: "90%", md: "86%", lg: "82%" },
          maxWidth: "850px",
          maxHeight: { xs: "90vh", sm: "88vh", md: "85vh" },
          overflowY: "auto",
          p: { xs: 3, sm: 3.5, md: 4, lg: 4.5 },
          display: "flex",
          flexDirection: "column",
          // gap: { xs: 2.5, sm: 3, md: 3.5 },
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
          borderRadius: { xs: "16px", sm: "18px", md: "20px" },
          border: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          // Better scrollbar styling
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: "#003366",
            fontWeight: "bold",
            fontSize: { xs: "22px", sm: "24px", md: "26px" },
            marginBottom: { xs: 1, sm: 1.5 },
          }}
        >
          {mode === "report" ? t("reportAnIssue", language) : t("yourFeedback", language)}
        </Typography>

        {mode === "report" ? (
          // Issue Report Form
          <div>
            <Typography
              variant="subtitle1"
              sx={{
                lineHeight: 1.5,
                fontSize: { xs: "15px", sm: "16px", md: "16px" },
                marginBottom: { xs: "12px", sm: "14px" },
                fontWeight: 600,
                color: "#333",
              }}
            >
              {t("describeIssue", language)}
            </Typography>
            <TextField
              multiline
              rows={5}
              fullWidth
              variant="outlined"
              placeholder={t("describeIssuePlaceholder", language)}
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "15px", sm: "15px" },
                  borderRadius: "10px",
                  padding: { xs: "12px", sm: "14px" },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "right",
                mt: 1,
                color: "#666",
              }}
            >
              {issueDescription.length} / 500
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                lineHeight: 1.5,
                fontSize: { xs: "15px", sm: "16px", md: "16px" },
                marginTop: { xs: "16px", sm: "20px" },
                marginBottom: { xs: "12px", sm: "14px" },
                fontWeight: 600,
                color: "#333",
              }}
            >
              {t("emailOptional", language)}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="your.email@example.com"
              value={reporterEmail}
              onChange={(e) => setReporterEmail(e.target.value)}
              type="email"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "15px", sm: "15px" },
                  borderRadius: "10px",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: "#666",
                fontStyle: "italic",
              }}
            >
              {t("emailFollowUp", language)}
            </Typography>
          </div>
        ) : (
          // Regular Feedback Form
          <>
            {renderBubbleScale(
              t("experienceQuestion", language),
              t("veryPoor", language),
              t("excellent", language),
              experience,
              setExperience
            )}

            <Divider sx={{ my: { xs: 1, sm: 1.5 } }} />

            {renderBubbleScale(
              t("usabilityQuestion", language),
              t("veryDifficult", language),
              t("veryEasy", language),
              usability,
              setUsability
            )}

            <Divider sx={{ my: { xs: 1, sm: 1.5 } }} />

            {renderBubbleScale(
              t("recommendQuestion", language),
              t("notAtAllLikely", language),
              t("extremelyLikely", language),
              recommend,
              setRecommend
            )}

            <Divider sx={{ my: { xs: 1, sm: 1.5 } }} />

            {renderBubbleScale(
              t("jobMatchQuestion", language),
              t("notAtAllSatisfied", language),
              t("extremelySatisfied", language),
              jobMatchSatisfaction,
              setJobMatchSatisfaction
            )}

            <Divider />
            <div>
              <Typography
                variant="subtitle1"
                sx={{
                  lineHeight: 1.5,
                  fontSize: { xs: "15px", sm: "16px", md: "16px" },
                  marginBottom: { xs: "12px", sm: "14px" },
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                {t("commentsQuestion", language)}
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder={t("yourComments", language)}
                value={openRemarks}
                onChange={(e) => setOpenRemarks(e.target.value)}
                inputProps={{ maxLength: 500 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: "15px", sm: "15px" },
                    borderRadius: "10px",
                    padding: { xs: "12px", sm: "14px" },
                  },
                }}
              />
            </div>
          </>
        )}

        <Divider />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 16,
          }}
        >
          <Button onClick={onClose} color="inherit" disabled={submitting}>
            {t("cancel", language)}
          </Button>{" "}
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? t("submitting", language) : t("submit", language)}
          </Button>
        </div>
      </Paper>
    </>
  );
};
